import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { PHILOSOPHER_PAGES, SLUG_TO_ID } from "../data/philosopherPages";
import { PHILOSOPHER_PROFILES } from "../data/philosopherProfiles";
import { PHILOSOPHERS } from "../data/philosophers";
import Footer from "../components/Footer";
import SEOHead from "../components/SEOHead";

// Amazon affiliate helper — same convention as the report reading path.
const AMAZON_BASE = "https://www.amazon.com/s?k=";
const AFFILIATE_TAG = "thephiloso037-20";
function extractTitle(entry) {
  const m = /"([^"]+)"/.exec(entry || "");
  return m ? m[1].trim() : entry;
}
function amazonUrl(title) {
  return `${AMAZON_BASE}${encodeURIComponent(title)}&tag=${encodeURIComponent(AFFILIATE_TAG)}`;
}

// Books mirror the backend BOOKS_BY_PHILOSOPHER — hard-coded here so the SEO
// page is renderable at build time without hitting the API. Keep in sync if
// the backend list ever changes.
const BOOKS = {
  aurelius: [
    '"Meditations" (Marcus Aurelius, Gregory Hays translation)',
    '"Letters from a Stoic" (Seneca)',
    '"Discourses and Selected Writings" (Epictetus)',
    '"The Obstacle Is the Way" (Ryan Holiday)',
    '"How to Think Like a Roman Emperor" (Donald Robertson)',
  ],
  nietzsche: [
    '"Thus Spoke Zarathustra" (Friedrich Nietzsche)',
    '"Beyond Good and Evil" (Friedrich Nietzsche)',
    '"The Gay Science" (Friedrich Nietzsche)',
    '"On the Genealogy of Morals" (Friedrich Nietzsche)',
    '"Twilight of the Idols" (Friedrich Nietzsche)',
  ],
  socrates: [
    '"Apology" (Plato)',
    '"Crito" (Plato)',
    '"Phaedo" (Plato)',
    '"Symposium" (Plato)',
    '"Memorabilia" (Xenophon)',
  ],
  epicurus: [
    '"Letters and Sayings of Epicurus" (Epicurus)',
    '"On the Nature of Things" (Lucretius)',
    '"The Art of Happiness" (Epicurus, Penguin)',
    '"How to Be an Epicurean" (Catherine Wilson)',
    '"The Swerve" (Stephen Greenblatt)',
  ],
  beauvoir: [
    '"The Second Sex" (Simone de Beauvoir)',
    '"The Ethics of Ambiguity" (Simone de Beauvoir)',
    '"The Mandarins" (Simone de Beauvoir)',
    '"Memoirs of a Dutiful Daughter" (Simone de Beauvoir)',
    '"Becoming Beauvoir" (Kate Kirkpatrick)',
  ],
  diogenes: [
    '"Lives of the Eminent Philosophers" (Diogenes Laertius)',
    '"The Cynic Philosophers" (Penguin Classics)',
    '"Cynics" (William Desmond)',
    '"Diogenes the Cynic: The War Against the World" (Luis E. Navia)',
    '"How to Say No: An Ancient Guide to the Art of Cynicism" (Diogenes, Princeton)',
  ],
  confucius: [
    '"The Analects" (Confucius, D.C. Lau translation)',
    '"The Analects" (Confucius, Simon Leys translation)',
    '"Mencius" (Mencius)',
    '"Confucius and Confucianism: The Essentials" (Lee Dian Rainey)',
    '"Confucius: The Secular as Sacred" (Herbert Fingarette)',
  ],
  arendt: [
    '"The Human Condition" (Hannah Arendt)',
    '"Eichmann in Jerusalem" (Hannah Arendt)',
    '"The Origins of Totalitarianism" (Hannah Arendt)',
    '"Men in Dark Times" (Hannah Arendt)',
    '"Hannah Arendt: A Life in Dark Times" (Anne C. Heller)',
  ],
};

export default function PhilosopherPage() {
  const { slug } = useParams();
  const id = SLUG_TO_ID[slug];
  // Track hotlink availability so we can hide the portrait cleanly if
  // upload.wikimedia.org is unreachable rather than leaving a broken-image icon.
  const [imgOk, setImgOk] = useState(true);

  if (!id) {
    return (
      <div className="relative z-10 mx-auto min-h-[100dvh] max-w-md px-6 py-20 text-center">
        <SEOHead noindex path={`/philosophers/${slug || ""}`} />
        <h1 className="font-serif text-3xl" style={{ color: "var(--gold)" }}>Not found</h1>
        <p className="mt-4" style={{ color: "var(--muted)" }}>
          <Link to="/philosophers" style={{ color: "var(--gold)" }}>Back to the 8 philosophers</Link>
        </p>
      </div>
    );
  }

  const page = PHILOSOPHER_PAGES[id];
  const profile = PHILOSOPHER_PROFILES[id];
  const base = PHILOSOPHERS.find((p) => p.id === id);
  const books = BOOKS[id] || [];

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Person",
        name: page.fullName,
        description: page.tagline,
        url: `https://thephilosophertest.com/philosophers/${page.slug}`,
        ...(page.imageUrl ? { image: page.imageUrl } : {}),
        sameAs: [
          `https://en.wikipedia.org/wiki/${page.fullName.replace(/ /g, "_")}`,
        ],
      },
      {
        "@type": "WebPage",
        name: page.metaTitle,
        description: page.metaDescription,
        url: `https://thephilosophertest.com/philosophers/${page.slug}`,
        about: { "@type": "Person", name: page.fullName },
        ...(page.imageUrl ? { primaryImageOfPage: { "@type": "ImageObject", url: page.imageUrl } } : {}),
      },
    ],
  };

  return (
    <div
      className="relative z-10 mx-auto min-h-[100dvh] w-full px-5 py-14"
      data-testid="philosopher-page"
    >
      <SEOHead
        title={page.metaTitle}
        description={page.metaDescription}
        path={`/philosophers/${page.slug}`}
        jsonLd={jsonLd}
        ogImage={page.imageUrl}
      />
      <div style={{ maxWidth: 720, margin: "0 auto" }}>
        <p
          className="text-[11px] font-medium uppercase"
          style={{ color: "var(--muted)", letterSpacing: "0.32em" }}
        >
          Philosopher Profile
        </p>

        {page.imageUrl && imgOk && (
          <figure className="mt-6 philosopher-portrait" data-testid="philosopher-portrait">
            <img
              src={page.imageUrl}
              alt={page.imageAlt || `Portrait of ${page.fullName}`}
              width="200"
              height="240"
              loading="eager"
              decoding="async"
              onError={() => setImgOk(false)}
              className="philosopher-portrait-img"
              data-testid="philosopher-portrait-img"
            />
          </figure>
        )}

        <h1
          className="mt-6 font-serif text-[36px] sm:text-[46px] leading-[1.08]"
          style={{ color: "var(--gold)" }}
          data-testid="philosopher-h1"
        >
          {page.fullName} Personality Type — {page.archetype}
        </h1>
        <p
          className="mt-4 font-serif italic text-[18px]"
          style={{ color: "var(--gold-light)" }}
        >
          {page.tagline}
        </p>

        <div className="report-body mt-8">
          <p>{page.intro}</p>

          <h2>Their view of life</h2>
          <p>{profile.view}</p>

          <h2>Movement</h2>
          <p>{profile.movement}.</p>

          <h2>How they moved through the world</h2>
          <p>{profile.move}</p>

          <h2>Legacy and why it still matters</h2>
          <p>{page.legacy}</p>

          {base && (
            <>
              <h2>Their trait fingerprint</h2>
              <p>
                In the six-trait model that powers The Philosopher Test, {page.fullName}'s
                signature is a precise fingerprint. Discipline sits at {base.vector.DIS},
                Curiosity at {base.vector.CUR}, Resilience at {base.vector.RES}, Empathy at{" "}
                {base.vector.EMP}, Ambition at {base.vector.AMB}, and Serenity at{" "}
                {base.vector.SER}. Read that vector out loud and you can hear the person: the
                traits that spike are the ones history remembers them for, and the traits
                that dip are the ones their critics use against them. The quiz computes the
                cosine similarity between your six numbers and theirs — if the angle between
                the two vectors is small, you get flagged as a {page.fullName.split(" ").slice(-1)[0]}{" "}
                match. It's not personality profiling in the clinical sense; it's a
                mathematical way of asking whether you and this historical mind bend the same
                way under the same pressures.
              </p>
            </>
          )}

          <h2>Three defining strengths</h2>
          <ul>
            {profile.strengths.map((s) => (
              <li key={s}>{s}</li>
            ))}
          </ul>

          <h2>Signs you think like {page.fullName}</h2>
          <ul data-testid="signs-list">
            {page.signs.map((s) => (
              <li key={s}>{s}</li>
            ))}
          </ul>

          <h2>Three quotes to know</h2>
          <blockquote>{page.quotes[0]}</blockquote>
          <blockquote>{page.quotes[1]}</blockquote>
          <blockquote>{page.quotes[2]}</blockquote>

          <h2>Where to start reading</h2>
          <p>
            If {page.fullName.split(" ").slice(-1)[0]}'s mind resonates with yours,
            these five books are the shortest path in. Books links are Amazon affiliate links.
          </p>
          <ul data-testid="books-list">
            {books.map((entry) => {
              const title = extractTitle(entry);
              const rest = entry.replace(`"${title}"`, "").trim();
              return (
                <li key={entry}>
                  <a
                    href={amazonUrl(title)}
                    target="_blank"
                    rel="noopener sponsored"
                    data-testid="philosopher-book-link"
                  >
                    &ldquo;{title}&rdquo;
                  </a>{" "}
                  {rest}
                </li>
              );
            })}
          </ul>
          <p
            className="text-[12px] italic"
            style={{ color: "var(--muted)" }}
            data-testid="affiliate-disclosure"
          >
            As an Amazon Associate, we earn from qualifying purchases. Book
            links on this page are affiliate links.
          </p>

          <h2>Opposite minds</h2>
          <p>
            If {page.fullName.split(" ").slice(-1)[0]} is one pole of your character
            sheet, the mirror poles are worth reading against:
          </p>
          <ul>
            {page.oppositeMinds.map((oid) => {
              const other = PHILOSOPHER_PAGES[oid];
              return (
                <li key={oid}>
                  <Link
                    to={`/philosophers/${other.slug}`}
                    style={{ color: "var(--gold)" }}
                    data-testid={`opposite-${oid}`}
                  >
                    {other.fullName} — {other.archetype}
                  </Link>
                </li>
              );
            })}
          </ul>

          <h2>Take the test</h2>
          <p>
            Are you actually {page.fullName.split(" ").slice(-1)[0]}? The Philosopher
            Test — 20 questions, 3 minutes — will tell you where your six traits
            place you among the eight great minds, including{" "}
            {page.fullName} ({page.archetype}) at{" "}
            {base ? `${Math.round(base.vector.DIS)}% Discipline / ${Math.round(base.vector.SER)}% Serenity` : "their trait ceiling"}.
          </p>
          <p>
            The result is a full character sheet across all six traits, a
            percentage match, a ranking of all eight philosophers by cosine
            similarity to your vector, and a shareable image card — all free.
            If {page.fullName.split(" ").slice(-1)[0]} is your top match, you'll get
            a private link to a longer AI-written report for a small fee, but
            you never have to pay to see who you are. Most people take the test
            expecting a novelty and stay for the diagnosis: the specific mind
            in history whose reflexes look most like their own, and the reading
            path that mind left behind for exactly this moment.
          </p>
        </div>

        <div className="mt-10 flex flex-col gap-3">
          <Link
            to="/"
            className="btn-primary text-center"
            data-testid="cta-take-test"
          >
            Take The Philosopher Test
          </Link>
          <Link
            to="/philosophers"
            className="btn-ghost text-center"
            data-testid="link-all-philosophers"
          >
            See all 8 philosophers
          </Link>
        </div>

        {page.imageUrl && page.imageCredit && (
          <p
            className="mt-10 text-[11px] leading-relaxed"
            style={{ color: "var(--muted)" }}
            data-testid="portrait-credit"
          >
            Portrait: {page.imageCredit.author},{" "}
            <a
              href={page.imageCredit.source}
              target="_blank"
              rel="noopener nofollow"
              style={{ color: "var(--muted)", textDecoration: "underline" }}
            >
              via Wikimedia Commons
            </a>
            ,{" "}
            {page.imageCredit.licenseUrl ? (
              <a
                href={page.imageCredit.licenseUrl}
                target="_blank"
                rel="noopener nofollow license"
                style={{ color: "var(--muted)", textDecoration: "underline" }}
              >
                {page.imageCredit.license}
              </a>
            ) : (
              page.imageCredit.license
            )}
            .
          </p>
        )}
      </div>
      <Footer />
    </div>
  );
}
